"use client"

import React, { useEffect, useMemo, useState } from "react"
import { Link } from "react-router-dom"

// Public API docs page for the BookingKoala -> ServiceFlow Zapier
// integration. Mounted as /docs/zapier, no auth required. Mirrors the
// three-column layout convention of modern API doc portals (Stripe,
// LeadBridge): slim top bar + left section nav + main content + right
// "On this page" TOC. Content is the markdown source committed to
// service-flow-backend/docs/bookingkoala-zapier-api.md, hand-rendered
// here so we don't need a markdown runtime dependency.

const STAGING_BASE = "https://service-flow-backend-staging-303f.up.railway.app"
const PROD_BASE = "https://api.service-flow.pro"

// ─ Side-nav structure ──────────────────────────────────────────────
// Groups + items; each `id` matches a section anchor rendered below.

const NAV = [
  {
    group: "Getting started",
    items: [
      { id: "overview", label: "Overview" },
      { id: "quick-start", label: "Quick start" },
      { id: "base-urls", label: "Base URLs" },
      { id: "authentication", label: "Authentication" },
    ],
  },
  {
    group: "Zapier",
    items: [
      { id: "zapier-setup", label: "Setup walkthrough" },
      { id: "event-types", label: "Event types" },
      { id: "payload-fields", label: "Payload field reference" },
    ],
  },
  {
    group: "API reference",
    items: [
      { id: "endpoints", label: "Endpoint reference" },
      { id: "response-schema", label: "Response schema" },
      { id: "error-codes", label: "Error codes" },
    ],
  },
  {
    group: "Advanced",
    items: [
      { id: "identity-resolution", label: "Identity resolution" },
      { id: "status-mapping", label: "Status mapping" },
      { id: "testing-curl", label: "Testing with curl" },
      { id: "troubleshooting", label: "Troubleshooting" },
      { id: "security", label: "Security notes" },
      { id: "operational", label: "Operational notes" },
    ],
  },
]

const FLAT_SECTIONS = NAV.flatMap((g) => g.items.map((i) => ({ ...i, group: g.group })))

// ─ Small primitives ────────────────────────────────────────────────

function MethodBadge({ method }) {
  const palette = {
    GET: "bg-emerald-100 text-emerald-700",
    POST: "bg-sky-100 text-sky-700",
    PUT: "bg-amber-100 text-amber-700",
    DELETE: "bg-rose-100 text-rose-700",
    PATCH: "bg-violet-100 text-violet-700",
  }
  return (
    <span
      className={`inline-block px-2 py-0.5 rounded text-[11px] font-semibold font-mono ${
        palette[method] || "bg-slate-100 text-slate-700"
      }`}
    >
      {method}
    </span>
  )
}

function CodeBlock({ language, children }) {
  const [copied, setCopied] = useState(false)
  const copy = () => {
    try {
      navigator.clipboard.writeText(String(children).trim())
      setCopied(true)
      setTimeout(() => setCopied(false), 1200)
    } catch {}
  }
  return (
    <div className="relative my-4 rounded-lg border border-slate-200 bg-slate-900 overflow-hidden">
      {language && (
        <div className="flex items-center justify-between px-3 py-1.5 border-b border-slate-800 bg-slate-800/50">
          <span className="text-[11px] font-mono text-slate-400 uppercase tracking-wider">
            {language}
          </span>
          <button
            onClick={copy}
            type="button"
            className="text-[11px] font-mono text-slate-400 hover:text-white transition"
          >
            {copied ? "Copied" : "Copy"}
          </button>
        </div>
      )}
      <pre className="p-4 text-[12.5px] leading-6 text-slate-100 overflow-x-auto font-mono">
        {String(children).trim()}
      </pre>
    </div>
  )
}

function Inline({ children }) {
  return (
    <code className="px-1.5 py-0.5 rounded bg-slate-100 text-[13px] font-mono text-slate-800">
      {children}
    </code>
  )
}

function H2({ id, children }) {
  return (
    <h2
      id={id}
      className="scroll-mt-24 text-[24px] font-bold text-slate-900 mt-14 mb-4 pt-4 border-t border-slate-100"
    >
      {children}
    </h2>
  )
}

function H3({ children }) {
  return <h3 className="text-[17px] font-semibold text-slate-900 mt-8 mb-3">{children}</h3>
}

function P({ children }) {
  return <p className="text-[14.5px] leading-7 text-slate-700 my-3">{children}</p>
}

function UL({ children }) {
  return <ul className="list-disc pl-6 my-3 space-y-1.5 text-[14.5px] text-slate-700">{children}</ul>
}

function Table({ headers, rows }) {
  return (
    <div className="overflow-x-auto my-4 border border-slate-200 rounded-lg">
      <table className="w-full text-[13.5px] text-left">
        <thead className="bg-slate-50 text-slate-700">
          <tr>
            {headers.map((h, i) => (
              <th key={i} className="px-3 py-2 font-semibold border-b border-slate-200 whitespace-nowrap">
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, ri) => (
            <tr key={ri} className="odd:bg-white even:bg-slate-50/50">
              {row.map((cell, ci) => (
                <td key={ci} className="px-3 py-2 align-top border-b border-slate-100 text-slate-700">
                  {cell}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

function Callout({ kind = "info", children }) {
  const styles = {
    info: "bg-sky-50 border-sky-200 text-sky-900",
    warn: "bg-amber-50 border-amber-200 text-amber-900",
    note: "bg-slate-50 border-slate-200 text-slate-800",
  }
  return (
    <div
      className={`my-4 border rounded-lg px-4 py-3 text-[13.5px] leading-6 ${
        styles[kind] || styles.info
      }`}
    >
      {children}
    </div>
  )
}

// ─ Layout ──────────────────────────────────────────────────────────

function Sidebar({ activeId, onSearch, search }) {
  const filter = search.trim().toLowerCase()
  return (
    <aside
      className="hidden lg:block w-[240px] shrink-0 border-r border-slate-200 bg-white h-full overflow-y-auto px-5 py-6"
    >
      <input
        type="text"
        value={search}
        onChange={(e) => onSearch(e.target.value)}
        placeholder="Search API reference..."
        className="w-full px-3 py-2 text-[13px] border border-slate-200 rounded-md bg-white placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-sky-500/40 focus:border-sky-400"
      />
      <nav className="mt-6 space-y-6">
        {NAV.map((g) => {
          const items = g.items.filter((i) => !filter || i.label.toLowerCase().includes(filter))
          if (items.length === 0) return null
          return (
            <div key={g.group}>
              <div className="text-[10.5px] font-bold tracking-widest uppercase text-slate-400 mb-2">
                {g.group}
              </div>
              <ul className="space-y-0.5">
                {items.map((i) => (
                  <li key={i.id}>
                    <a
                      href={`#${i.id}`}
                      className={`block px-2 py-1.5 rounded text-[13.5px] transition ${
                        activeId === i.id
                          ? "bg-sky-50 text-sky-700 font-semibold"
                          : "text-slate-600 hover:text-slate-900 hover:bg-slate-50"
                      }`}
                    >
                      {i.label}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          )
        })}
      </nav>
    </aside>
  )
}

function RightToc({ activeId }) {
  return (
    <aside
      className="hidden xl:block w-[220px] shrink-0 pl-6 h-full overflow-y-auto py-6"
    >
      <div className="text-[10.5px] font-bold tracking-widest uppercase text-slate-400 mb-3">
        On this page
      </div>
      <ul className="space-y-1.5">
        {FLAT_SECTIONS.map((s) => (
          <li key={s.id}>
            <a
              href={`#${s.id}`}
              className={`block text-[12.5px] leading-5 transition ${
                activeId === s.id
                  ? "text-sky-700 font-semibold"
                  : "text-slate-500 hover:text-slate-900"
              }`}
            >
              {s.label}
            </a>
          </li>
        ))}
      </ul>
    </aside>
  )
}

function Header() {
  return (
    <header className="shrink-0 h-14 border-b border-slate-200 bg-white">
      <div className="h-full px-5 flex items-center gap-6">
        <Link to="/" className="flex items-center gap-2">
          <span className="inline-flex items-center justify-center w-8 h-8 rounded-md bg-slate-900 text-white font-bold text-[13px]">
            SF
          </span>
          <span className="font-semibold text-slate-900">ServiceFlow</span>
          <span className="ml-1 text-[11px] font-mono px-1.5 py-0.5 rounded bg-slate-100 text-slate-600">
            Docs
          </span>
        </Link>
        <nav className="hidden md:flex items-center gap-5 ml-6 text-[13.5px]">
          <a href="#endpoints" className="text-slate-900 border-b-2 border-sky-500 pb-[18px]">
            REST API Reference
          </a>
          <a href="#zapier-setup" className="text-slate-600 hover:text-slate-900">
            Zapier Setup Guide
          </a>
        </nav>
        <div className="flex-1" />
        <Link
          to="/signin"
          className="hidden sm:inline-block text-[13.5px] text-slate-600 hover:text-slate-900"
        >
          Sign in
        </Link>
        <Link
          to="/signin?next=/settings/integrations/bookingkoala-zapier"
          className="inline-flex items-center px-3.5 py-1.5 rounded-md bg-sky-600 hover:bg-sky-700 text-white text-[13px] font-semibold transition"
        >
          Get API Key
        </Link>
      </div>
    </header>
  )
}

// ─ Content ─────────────────────────────────────────────────────────

function Overview() {
  return (
    <>
      <div className="text-[12px] font-bold tracking-widest uppercase text-sky-600 mb-2">
        Developer documentation
      </div>
      <h1 className="text-[36px] font-extrabold text-slate-900 leading-tight">
        BookingKoala &rarr; ServiceFlow API
      </h1>
      <p className="mt-4 text-[16px] text-slate-600 leading-7">
        Live one-way sync from BookingKoala into ServiceFlow via a Zapier
        <Inline>Webhooks by Zapier</Inline> POST action. Five triggers map to
        five event endpoints &mdash; <Inline>new</Inline>,{" "}
        <Inline>updated</Inline>, <Inline>completed</Inline>,{" "}
        <Inline>cancelled</Inline>, <Inline>deleted</Inline>.
      </p>

      <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-3">
        {[
          { label: "Protocol", value: "HTTPS / REST" },
          { label: "Authentication", value: "Bearer API key" },
          { label: "Format", value: "application/json" },
          { label: "Default rate limit", value: "n/a (planned)" },
        ].map((c) => (
          <div key={c.label} className="rounded-lg border border-slate-200 bg-white px-4 py-3">
            <div className="text-[10.5px] font-bold tracking-widest uppercase text-slate-400">
              {c.label}
            </div>
            <div className="text-[14px] font-mono font-semibold text-slate-900 mt-1">
              {c.value}
            </div>
          </div>
        ))}
      </div>

      <H2 id="overview">Overview</H2>
      <H3>What this integration does</H3>
      <UL>
        <li>
          Every booking you create, change, finish, cancel or delete in
          BookingKoala flows into ServiceFlow as a job within seconds.
        </li>
        <li>
          Customers are matched (by stable BK customer id &rarr; phone &rarr;
          email) or created.
        </li>
        <li>
          Cleaners are matched by their <Inline>external_crew_id</Inline>{" "}
          &rarr; email &rarr; exact full name. <strong>Never auto-created</strong>
          &nbsp;&mdash; unknown providers are returned in the response for you
          to fix.
        </li>
        <li>
          A ServiceFlow job's <Inline>contact_info.external_id</Inline> always
          equals the BookingKoala booking id, so re-firing an event updates the
          same job in place. No duplicates.
        </li>
      </UL>

      <H3>What this integration does not do</H3>
      <UL>
        <li>No push back to BookingKoala. Changes made in SF stay in SF.</li>
        <li>
          No historical backfill. Use the BookingKoala CSV importer for one-time
          loads &mdash; both paths share identity so they never collide.
        </li>
        <li>
          No cleaner auto-provisioning. A booking with an unknown provider
          syncs the job but leaves it unassigned until you add the cleaner.
        </li>
      </UL>
    </>
  )
}

function QuickStart() {
  return (
    <>
      <H2 id="quick-start">Quick start &mdash; 5 minutes</H2>
      <ol className="list-decimal pl-6 my-3 space-y-3 text-[14.5px] text-slate-700 leading-7">
        <li>
          <strong>Mint your API key.</strong> Click <strong>Generate key</strong>{" "}
          in SF Settings &rarr; Integrations &rarr; BookingKoala Zapier, or
          POST to <Inline>/api/integrations/bookingkoala/api-key</Inline> with
          your SF JWT.
        </li>
        <li>
          <strong>Create 5 Zaps in Zapier</strong>, one per BK trigger (New /
          Updated / Completed / Cancelled / Deleted). Each uses{" "}
          <Inline>Webhooks by Zapier</Inline> &rarr; <strong>POST</strong> with
          the matching event endpoint and your{" "}
          <Inline>Authorization: Bearer sf_bk_&lt;key&gt;</Inline> header.
        </li>
        <li>
          <strong>Fire a test booking</strong> in BK or from Zapier's test
          panel. A <Inline>200 OK</Inline> with{" "}
          <Inline>&#123; ok: true, action: &quot;created&quot; &#125;</Inline>{" "}
          means it landed.
        </li>
        <li>
          <strong>Verify in SF</strong> &mdash; the job appears on the
          schedule, the customer under Customers, and the activity timeline
          shows the BK sync.
        </li>
      </ol>
    </>
  )
}

function BaseUrls() {
  return (
    <>
      <H2 id="base-urls">Base URLs &amp; environments</H2>
      <P>
        All API paths below are relative to the environment base URL. Always use
        HTTPS for production requests.
      </P>
      <Table
        headers={["Environment", "Gateway host", "Usage"]}
        rows={[
          [
            "Production",
            <Inline key="p">{PROD_BASE}</Inline>,
            "Live customer workflows",
          ],
          [
            "Staging",
            <Inline key="s">{STAGING_BASE}</Inline>,
            "Pre-release contract testing",
          ],
        ]}
      />
    </>
  )
}

function Authentication() {
  return (
    <>
      <H2 id="authentication">Authentication</H2>
      <H3>API keys</H3>
      <UL>
        <li>One active key per tenant. Rotating a key revokes the old one atomically.</li>
        <li>
          Keys are shown <strong>once</strong> at creation. Only an SHA-256 hash
          is stored. We cannot recover a lost key &mdash; rotate.
        </li>
        <li>
          Keys always start with the prefix <Inline>sf_bk_</Inline>.
        </li>
      </UL>

      <H3>Accepted auth headers</H3>
      <P>Any of these works &mdash; Zapier's Webhooks action can be configured with any:</P>
      <CodeBlock language="http">{`Authorization: Bearer sf_bk_<key>
X-API-Key: sf_bk_<key>
X-SF-API-Key: sf_bk_<key>`}</CodeBlock>

      <H3>Minting a key via API</H3>
      <CodeBlock language="bash">{`curl -X POST "${PROD_BASE}/api/integrations/bookingkoala/api-key" \\
  -H "Authorization: Bearer <your-SF-user-JWT>" \\
  -H "Content-Type: application/json"`}</CodeBlock>
      <P>Response:</P>
      <CodeBlock language="json">{`{
  "apiKey": "sf_bk_1-4ZkoCoKJ-4vY4NufKguHKSrL2fwQrU",
  "keyPrefix": "sf_bk_1-4Z",
  "createdAt": "2026-10-13T14:22:00.000Z"
}`}</CodeBlock>
      <Callout kind="warn">
        Only account owners and admins can mint keys. Team-member tokens with
        <Inline>role != owner/admin</Inline> get <Inline>403</Inline>.
      </Callout>

      <H3>Revoking a key</H3>
      <CodeBlock language="bash">{`curl -X DELETE "${PROD_BASE}/api/integrations/bookingkoala/api-key" \\
  -H "Authorization: Bearer <your-SF-user-JWT>"`}</CodeBlock>
    </>
  )
}

function ZapierSetup() {
  return (
    <>
      <H2 id="zapier-setup">Zapier setup walkthrough</H2>
      <P>
        A full BookingKoala &rarr; ServiceFlow sync is <strong>five Zaps</strong>
        , one per trigger. You can start with just <strong>New Booking</strong>{" "}
        and <strong>Updated Booking</strong> (the two that count toward the
        &quot;active&quot; status in the SF setup UI) and add the rest later.
      </P>
      <H3>1. Trigger</H3>
      <P>
        Pick the BookingKoala trigger that matches the event, then click{" "}
        <strong>Test trigger</strong> so Zapier captures a sample payload.
      </P>
      <H3>2. Action &mdash; Webhooks by Zapier (POST)</H3>
      <Table
        headers={["Field", "Value"]}
        rows={[
          [
            <Inline key="u">URL</Inline>,
            <code key="uv">{`<base-url>/api/integrations/bookingkoala/zapier/bookings/<eventType>`}</code>,
          ],
          [<Inline key="pt">Payload Type</Inline>, <Inline key="json">json</Inline>],
          [
            <Inline key="d">Data</Inline>,
            "Map every BK field as a top-level key = value pair. Use BK's native field codes (booking_id, booking_iso_date_time, customer_email, provider_details…). The API accepts snake_case, Title Case, camelCase and CSV columns interchangeably.",
          ],
          [<Inline key="w">Wrap Request In Array</Inline>, <Inline key="n">no</Inline>],
          [
            <Inline key="h">Headers</Inline>,
            <>
              <div><Inline>Authorization: Bearer sf_bk_&lt;your-key&gt;</Inline></div>
              <div className="mt-1"><Inline>Content-Type: application/json</Inline></div>
            </>,
          ],
        ]}
      />
      <P>Example URLs per event:</P>
      <CodeBlock language="http">{`POST .../zapier/bookings/new
POST .../zapier/bookings/updated
POST .../zapier/bookings/completed
POST .../zapier/bookings/cancelled
POST .../zapier/bookings/deleted`}</CodeBlock>

      <H3>3. Test action</H3>
      <P>
        Zapier's <strong>Test action</strong> button fires one POST with the
        sample payload. A successful call returns <Inline>200</Inline> with{" "}
        <Inline>&#123; ok: true &#125;</Inline>. If the response is non-200,
        copy <Inline>error</Inline>/<Inline>message</Inline> and see{" "}
        <a href="#error-codes" className="text-sky-600 hover:underline">Error codes</a>.
      </P>

      <H3>Minimum fields per event</H3>
      <Table
        headers={["Event", "Required fields"]}
        rows={[
          ["new", <><Inline>booking_id</Inline>, plus date (<Inline>booking_iso_date_time</Inline> OR <Inline>booking_date</Inline>+<Inline>booking_time</Inline>)</>],
          ["updated", <><Inline>booking_id</Inline>, plus whichever fields you want to change</>],
          ["completed", <Inline>booking_id</Inline>],
          ["cancelled", <Inline>booking_id</Inline>],
          ["deleted", <Inline>booking_id</Inline>],
        ]}
      />
    </>
  )
}

function EventTypes() {
  return (
    <>
      <H2 id="event-types">Event types</H2>
      <H3>Semantics</H3>
      <Table
        headers={["Event", "Behavior"]}
        rows={[
          ["new", "Insert the job. If one already exists for this booking id, fall through to updated (idempotent). Default status: pending."],
          ["updated", "Patch the existing job. If none exists yet, create one as if it were new."],
          ["completed", "Mark the job completed and generate per-cleaner ledger entries (hourly, commission, tips)."],
          ["cancelled", "Flip status to cancelled. The job stays visible."],
          ["deleted", "Tag the job bk-deleted, keep the row for audit. The default SF UI views filter it out."],
        ]}
      />

      <H3>Event aliases</H3>
      <P>The API normalizes several alternate names:</P>
      <Table
        headers={["Canonical", "Also accepted"]}
        rows={[
          ["new", "created, create, booking_created, new_booking"],
          ["updated", "update, booking_updated, rescheduled"],
          ["completed", "complete, booking_completed"],
          ["cancelled", "canceled, cancel, booking_cancelled, booking_canceled"],
          ["deleted", "delete, booking_deleted"],
        ]}
      />
    </>
  )
}

function PayloadFields() {
  return (
    <>
      <H2 id="payload-fields">Payload field reference</H2>
      <P>
        Every field accepts multiple key spellings &mdash; snake_case, Title Case,
        camelCase, and BK's CSV column names. Internally the handler lowercases
        and strips non-alphanumerics, so <Inline>Booking ID</Inline>,{" "}
        <Inline>booking_id</Inline>, <Inline>bookingId</Inline> and{" "}
        <Inline>booking-id</Inline> all resolve to the same slot.
      </P>

      <H3>Core</H3>
      <Table
        headers={["Field", "Accepted keys", "Required?"]}
        rows={[
          ["Booking id", "id, booking_id, bookingId, Booking ID, booking_ids", <strong key="r">yes</strong>],
          ["Status", "status, booking_status, Booking status", "no"],
        ]}
      />

      <H3>Scheduling</H3>
      <Table
        headers={["Field", "Accepted keys"]}
        rows={[
          ["ISO date+time", "booking_iso_date_time, Booking Iso Date Time, iso_date_time, booking_start_date_time, start_at"],
          ["Date only", "booking_date, service_date, date"],
          ["Time only", "booking_time, time, start_time"],
          ["Adjusted / estimated duration", "adjusted_time, Adjusted Time, estimated_job_length, Estimated Job Length, duration, estimated_duration"],
        ]}
      />
      <Callout kind="note">
        <strong>Date rules.</strong> Dates may be ISO (<Inline>2026-10-14T10:00:00</Inline>),
        ISO with offset, or a plain date. Values with no offset pass through as
        wall-clock (SF stores jobs as wall-clock). Values with an offset are
        converted to the tenant's timezone.
        <br />
        <strong>Duration rule.</strong> <Inline>Adjusted Time</Inline> overrides{" "}
        <Inline>Estimated Job Length</Inline> when both are present &mdash; never
        additive.
      </Callout>

      <H3>Service</H3>
      <Table
        headers={["Field", "Accepted keys"]}
        rows={[
          ["Service name", "service, service_name, Service"],
          ["Service category", "service_category, Service Category, category, industry"],
        ]}
      />

      <H3>Customer</H3>
      <Table
        headers={["Field", "Accepted keys"]}
        rows={[
          ["Customer id (stable identity)", "customer_id, customerId, Customer Id"],
          ["First name", "customer_first_name, first_name, First name"],
          ["Last name", "customer_last_name, last_name, Last name"],
          ["Full name (fallback)", "customer_name, full_name, Full name, name"],
          ["Email", "customer_email, email, Email, email_address"],
          ["Phone", "customer_phone, phone, Phone, phone_number, mobile"],
          ["Street", "address, Address, street_address, street, customer_address"],
          ["Suite / apt", "apt, Apt, apt_no, Apt. No., suite, unit"],
          ["City", "city, City"],
          ["State", "state, State"],
          ["Zip", "zip, zip_code, Zip/Postal code, postal_code, zipcode"],
        ]}
      />

      <H3>Providers (cleaners)</H3>
      <P>BK emits providers in three different shapes. All three are supported.</P>
      <P><strong>Shape A &mdash; array of objects</strong> (modern BK webhook)</P>
      <CodeBlock language="json">{`"provider_details": [
  { "id": 16, "name": "Andrii P", "email": "a@b.c" },
  { "id": 22, "name": "Natalya P" }
]`}</CodeBlock>
      <P><strong>Shape B &mdash; parallel CSV strings</strong> (Zapier's flattened arrays)</P>
      <CodeBlock language="json">{`"provider_details_id": "16,22",
"provider_details_name": "Andrii P,Natalya P",
"provider_details_email": "a@b.c,n@b.c"`}</CodeBlock>
      <P><strong>Shape C &mdash; BK CSV export</strong> &quot;Provider/team&quot; column</P>
      <CodeBlock language="json">{`"Provider/team": "16: Andrii P, 22: Natalya P"`}</CodeBlock>

      <H3>Money</H3>
      <Table
        headers={["Field", "Accepted keys"]}
        rows={[
          ["Total", "final_amount, Final amount (USD), booking_total, total, total_amount, amount, price, service_total"],
          ["Tip", "tip, tip_amount, Tip, tips"],
          ["Payment method", "payment_method, Payment method"],
        ]}
      />
      <Callout kind="note">
        Dollar strings (<Inline>$299.50</Inline>, <Inline>$1,250</Inline>) are parsed automatically.
      </Callout>

      <H3>Notes</H3>
      <P>
        Any of these (joined by double newlines, deduped):{" "}
        <Inline>booking_note</Inline>, <Inline>Booking note</Inline>,{" "}
        <Inline>customer_note</Inline>, <Inline>notes</Inline>, <Inline>note</Inline>,{" "}
        <Inline>special_notes</Inline>, <Inline>Special notes</Inline>,{" "}
        <Inline>provider_note</Inline>, <Inline>Provider note</Inline>.
      </P>

      <H3>Frequency</H3>
      <Table
        headers={["BK string (case-insensitive, substring OK)", "SF recurring_frequency"]}
        rows={[
          ["One-Time, One Time, Once", "null (one-time)"],
          ["Every Other Week, Every 2 Weeks, Bi-Weekly", "bi-weekly"],
          ["Weekly", "weekly"],
          ["Every 4 Weeks, Monthly", "monthly"],
          ["anything else recurring", "custom"],
        ]}
      />
    </>
  )
}

function Endpoints() {
  const rows = [
    ["POST", "/api/integrations/bookingkoala/api-key", "SF JWT", "Mint a new tenant Zapier key (revokes prior)"],
    ["DELETE", "/api/integrations/bookingkoala/api-key", "SF JWT", "Revoke the active key"],
    ["GET", "/api/integrations/bookingkoala/status", "SF JWT", "Key metadata + synced-job count + 10 most-recent synced jobs"],
    ["GET", "/api/integrations/bookingkoala/setup-progress", "SF JWT", "Setup stage + per-event last-seen"],
    ["GET", "/api/integrations/bookingkoala/zapier/me", "API key", "\"Is my key valid\" check"],
    ["POST", "/api/integrations/bookingkoala/zapier/bookings", "API key", "Receive a BK event. eventType comes in the body."],
    ["POST", "/api/integrations/bookingkoala/zapier/bookings/:eventType", "API key", "Receive a BK event. eventType is the last URL segment."],
  ]
  return (
    <>
      <H2 id="endpoints">Endpoint reference</H2>
      <div className="overflow-x-auto my-4 border border-slate-200 rounded-lg">
        <table className="w-full text-[13.5px] text-left">
          <thead className="bg-slate-50 text-slate-700">
            <tr>
              <th className="px-3 py-2 font-semibold border-b border-slate-200">Method</th>
              <th className="px-3 py-2 font-semibold border-b border-slate-200">Path</th>
              <th className="px-3 py-2 font-semibold border-b border-slate-200">Auth</th>
              <th className="px-3 py-2 font-semibold border-b border-slate-200">Purpose</th>
            </tr>
          </thead>
          <tbody>
            {rows.map(([m, p, a, d]) => (
              <tr key={p + m} className="odd:bg-white even:bg-slate-50/50">
                <td className="px-3 py-2 border-b border-slate-100">
                  <MethodBadge method={m} />
                </td>
                <td className="px-3 py-2 border-b border-slate-100 font-mono text-[12.5px] text-slate-800">
                  {p}
                </td>
                <td className="px-3 py-2 border-b border-slate-100 text-slate-700">{a}</td>
                <td className="px-3 py-2 border-b border-slate-100 text-slate-700">{d}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <H3>POST /zapier/bookings/:eventType</H3>
      <P>Request body: the raw BK trigger payload (or <Inline>&#123; &quot;payload&quot;: &#123;...&#125; &#125;</Inline>).</P>
      <CodeBlock language="json">{`{
  "ok": true,
  "eventType": "new",
  "bookingId": "bk-test-001",
  "action": "created",
  "jobId": 142951,
  "status": "pending",
  "customerId": 23691,
  "customerMatch": "created",
  "teamMemberIds": [2692],
  "unmatchedProviders": []
}`}</CodeBlock>

      <H3>GET /zapier/me</H3>
      <CodeBlock language="json">{`{
  "ok": true,
  "userId": 24,
  "email": "owner@example.com",
  "businessName": "Spotless Homes"
}`}</CodeBlock>

      <H3>GET /status</H3>
      <CodeBlock language="json">{`{
  "connected": true,
  "keyPrefix": "sf_bk_1-4Z",
  "keyCreatedAt": "2026-10-01T12:00:00Z",
  "lastUsedAt": "2026-10-13T14:21:04Z",
  "syncedJobs": 42,
  "recent": [ ... ]
}`}</CodeBlock>

      <H3>GET /setup-progress</H3>
      <P>
        <Inline>stage</Inline> lifecycle: <Inline>not_configured</Inline> &rarr;{" "}
        <Inline>configured</Inline> &rarr; <Inline>tested</Inline> &rarr;{" "}
        <Inline>active</Inline>. Reaches <Inline>active</Inline> once both{" "}
        <Inline>new</Inline> and <Inline>updated</Inline> have fired at least
        once in the last 30 days.
      </P>
    </>
  )
}

function ResponseSchema() {
  return (
    <>
      <H2 id="response-schema">Response schema</H2>
      <H3>Success &mdash; 200</H3>
      <CodeBlock language="json">{`{
  "ok": true,
  "eventType": "updated",
  "bookingId": "bk-9876",
  "action": "updated",
  "jobId": 142951,
  "status": "pending",
  "previousStatus": "pending",
  "customerMatch": "matched_phone",
  "teamMemberIds": [2692, 2695],
  "unmatchedProviders": []
}`}</CodeBlock>
      <P><Inline>action</Inline> can be:</P>
      <Table
        headers={["action", "Meaning"]}
        rows={[
          ["created", "New job was inserted"],
          ["updated", "Existing job was patched in place"],
          ["cancelled", "Status transitioned to cancelled"],
          ["completed", "Status transitioned to completed, ledger entries created"],
          ["deleted", "Job tagged bk-deleted; row preserved for audit"],
          ["stale_skipped", "Older event delivered after a newer one — no-op"],
          ["ignored_unknown_booking", "cancelled/deleted for a booking id that never synced"],
        ]}
      />
      <P>
        <Inline>customerMatch</Inline> can be:{" "}
        <Inline>matched_bookingkoala_customer_id</Inline>,{" "}
        <Inline>matched_phone</Inline>, <Inline>matched_email</Inline>,{" "}
        <Inline>created</Inline>, <Inline>no_customer_data</Inline>.
      </P>

      <H3>Error &mdash; 4xx/5xx</H3>
      <CodeBlock language="json">{`{
  "error": "invalid_payload",
  "message": "Booking has no date — map \\"Booking Iso Date Time\\" (or Booking Date + Booking Time) in the Zap"
}`}</CodeBlock>
    </>
  )
}

function ErrorCodes() {
  return (
    <>
      <H2 id="error-codes">Error codes</H2>
      <Table
        headers={["HTTP", "error", "Likely cause / fix"]}
        rows={[
          ["400", "invalid_event_type", "URL ends in something other than new|updated|completed|cancelled|deleted, or body eventType isn't a known alias."],
          ["400", "invalid_payload", "Normalizer couldn't extract required fields. Message names what's missing."],
          ["401", "missing_api_key", "No Authorization / X-API-Key / X-SF-API-Key header present."],
          ["401", "invalid_api_key", "Key doesn't match an active row. May have been rotated."],
          ["403", "Only the account owner or an admin can manage this integration", "A team-member token tried to call /api-key or /status."],
          ["500", "sync_failed", "Downstream DB write failed. Check Railway log line prefixed [BK-Zapier]."],
          ["500", "api_key_auth_error", "Internal error in the auth middleware."],
          ["503", "api_key_lookup_failed", "Supabase was unreachable at the auth step. Retryable."],
          ["503", "migration_missing", "Migration 084 hasn't been applied on the connected database. Run migrations/084_bookingkoala_zapier.sql."],
        ]}
      />
    </>
  )
}

function IdentityResolution() {
  return (
    <>
      <H2 id="identity-resolution">Identity resolution</H2>
      <H3>Jobs</H3>
      <P>
        Key: <Inline>jobs.contact_info-&gt;&gt;&apos;external_id&apos;</Inline> = BK booking id.
        Same key the CSV importer writes, so a historical CSV load and the live
        Zap share identity &mdash; a booking imported from CSV and then updated
        in BK updates the <strong>same</strong> SF job.
      </P>

      <H3>Customers</H3>
      <P>Matching order:</P>
      <ol className="list-decimal pl-6 my-3 space-y-1.5 text-[14.5px] text-slate-700 leading-7">
        <li><Inline>customers.bookingkoala_customer_id</Inline> = BK customer id (migration 084).</li>
        <li>Phone &mdash; last 10 digits normalized.</li>
        <li>Email &mdash; case-insensitive exact.</li>
        <li>Create &mdash; if none above matched and the payload has any customer data at all, insert with <Inline>source = booking_koala</Inline>.</li>
      </ol>
      <Callout kind="note">
        Matched customers are patched for null fields only &mdash; if the SF
        business owner edited the phone or address, the inbound Zap payload will
        NOT overwrite it.
      </Callout>

      <H3>Cleaners (team members)</H3>
      <ol className="list-decimal pl-6 my-3 space-y-1.5 text-[14.5px] text-slate-700 leading-7">
        <li><Inline>team_members.settings.external_crew_id</Inline> = BK provider id.</li>
        <li>Email &mdash; case-insensitive exact.</li>
        <li>Exact full-name match &mdash; only when exactly one team member matches.</li>
      </ol>
      <Callout kind="warn">
        <strong>Cleaners are never auto-created.</strong> An unmatched provider
        surfaces in the response as{" "}
        <Inline>&quot;unmatchedProviders&quot;: [&#123; id, name, email &#125;]</Inline>.
        The job still syncs; the cleaner just isn't assigned until you add them
        in SF.
      </Callout>
    </>
  )
}

function StatusMapping() {
  return (
    <>
      <H2 id="status-mapping">Status mapping</H2>
      <Table
        headers={["BK event type", "BK raw status", "Resulting SF status"]}
        rows={[
          ["new", "(any)", "pending (unless BK itself says completed/cancelled)"],
          ["updated", "(SF owns in-field progress)", "no change unless BK says completed or cancelled, or re-opens a cancellation"],
          ["completed", "(any)", "completed"],
          ["cancelled", "(any)", "cancelled"],
          ["deleted", "(any)", "tagged bk-deleted, status unchanged"],
        ]}
      />
      <Callout kind="note">
        <strong>Why updated doesn't move status</strong>: once a cleaner is en
        route / started / finished in the SF app, we don't want a stray BK
        webhook to bump the job back to &quot;scheduled&quot; just because the
        office edited an unrelated note.
      </Callout>
    </>
  )
}

function TestingCurl() {
  return (
    <>
      <H2 id="testing-curl">Testing with curl</H2>
      <P>Replace <Inline>sf_bk_YOUR_KEY</Inline> with your real key.</P>

      <H3>1. Check your key</H3>
      <CodeBlock language="bash">{`curl "${STAGING_BASE}/api/integrations/bookingkoala/zapier/me" \\
  -H "Authorization: Bearer sf_bk_YOUR_KEY"`}</CodeBlock>

      <H3>2. Create a job</H3>
      <CodeBlock language="bash">{`curl -X POST "${STAGING_BASE}/api/integrations/bookingkoala/zapier/bookings/new" \\
  -H "Authorization: Bearer sf_bk_YOUR_KEY" \\
  -H "Content-Type: application/json" \\
  -d '{
    "booking_id": "bk-test-001",
    "booking_iso_date_time": "2026-10-14T10:00:00",
    "customer": {
      "customer_id": "bk-cust-001",
      "first_name": "Test",
      "last_name": "FromZapier",
      "email": "test@example.com",
      "phone": "+15555550123"
    },
    "service_name": "Deep Cleaning",
    "duration": 180,
    "total": 250,
    "address": { "street": "123 Test Ave", "city": "Jacksonville", "state": "FL", "zip": "32221" }
  }'`}</CodeBlock>

      <H3>3. Update the same job</H3>
      <CodeBlock language="bash">{`curl -X POST "${STAGING_BASE}/api/integrations/bookingkoala/zapier/bookings/updated" \\
  -H "Authorization: Bearer sf_bk_YOUR_KEY" \\
  -H "Content-Type: application/json" \\
  -d '{ "booking_id": "bk-test-001", "total": 275 }'`}</CodeBlock>

      <H3>4. Cancel it</H3>
      <CodeBlock language="bash">{`curl -X POST "${STAGING_BASE}/api/integrations/bookingkoala/zapier/bookings/cancelled" \\
  -H "Authorization: Bearer sf_bk_YOUR_KEY" \\
  -H "Content-Type: application/json" \\
  -d '{ "booking_id": "bk-test-001" }'`}</CodeBlock>

      <H3>5. Delete it</H3>
      <CodeBlock language="bash">{`curl -X POST "${STAGING_BASE}/api/integrations/bookingkoala/zapier/bookings/deleted" \\
  -H "Authorization: Bearer sf_bk_YOUR_KEY" \\
  -H "Content-Type: application/json" \\
  -d '{ "booking_id": "bk-test-001" }'`}</CodeBlock>

      <Callout kind="warn">
        <strong>Windows CMD:</strong> single quotes don't work. Use double
        quotes and escape every <Inline>&quot;</Inline> inside the body with{" "}
        <Inline>\&quot;</Inline>.
      </Callout>
    </>
  )
}

function Troubleshooting() {
  return (
    <>
      <H2 id="troubleshooting">Troubleshooting</H2>

      <H3>invalid_payload: Booking has no date</H3>
      <P>
        Your Zap's <strong>Data</strong> section isn't sending any of{" "}
        <Inline>booking_iso_date_time</Inline>,{" "}
        <Inline>booking_date</Inline>+<Inline>booking_time</Inline>, or{" "}
        <Inline>start_at</Inline>. Map one of them from the BK trigger.
      </P>

      <H3>invalid_payload: no booking id</H3>
      <P>
        Add a <Inline>booking_id</Inline> field in Zapier's &quot;Set up
        action&quot; step and map it to <Inline>&#123;&#123;Booking ID&#125;&#125;</Inline>.
      </P>

      <H3>invalid_api_key</H3>
      <P>
        Your key was rotated. Regenerate under Settings &rarr; Integrations
        &rarr; BookingKoala Zapier and update the <Inline>Authorization</Inline>{" "}
        header on every Zap.
      </P>

      <H3>migration_missing</H3>
      <P>
        The connected database is missing migration 084. Open the Supabase
        project the backend is pointed at and run{" "}
        <Inline>migrations/084_bookingkoala_zapier.sql</Inline>, then retry.
      </P>

      <H3>Customer appears twice</H3>
      <P>
        Neither the <Inline>bookingkoala_customer_id</Inline> nor the phone/email
        matched at the time of the first sync. Merge the duplicates in SF;
        subsequent BK events will update the merged record because the first
        sync stamped <Inline>bookingkoala_customer_id</Inline> on it.
      </P>

      <H3>A provider shows in unmatchedProviders</H3>
      <P>
        That BK provider isn&apos;t a team member in SF. Either add the cleaner
        with the right <Inline>external_crew_id</Inline>, match by email, or
        match by exact full name. Re-fire the Zap and the assignment sticks.
      </P>

      <H3>Where to look in Railway logs</H3>
      <P>Every sync writes a single structured log line:</P>
      <CodeBlock language="log">{`[BK-Zapier] user=24 event=updated booking=bk-9876 action=updated job=142951 ms=187`}</CodeBlock>
      <P>Search for the booking id to see its full history.</P>
    </>
  )
}

function Security() {
  return (
    <>
      <H2 id="security">Security notes</H2>
      <UL>
        <li>
          <strong>Key storage.</strong> Plaintext is returned once at{" "}
          <Inline>POST /api-key</Inline> and never again. The DB stores only the
          SHA-256 hash. We cannot show the key later &mdash; rotate.
        </li>
        <li>
          <strong>Lookup is by hash.</strong> No plaintext comparison exists, so
          there is no timing-attack surface at auth time.
        </li>
        <li>
          <strong>Scope.</strong> A key authenticates one tenant only. It cannot
          see other tenants' data.
        </li>
        <li>
          <strong>Rotation.</strong> <Inline>POST /api-key</Inline> revokes the
          active key before minting the new one atomically. There is never a
          window with two active keys.
        </li>
        <li>
          <strong>Transport.</strong> TLS only. HTTP requests redirect to HTTPS.
        </li>
        <li>
          <strong>Rate limiting.</strong> <em>Not yet enforced.</em> Treat your
          key like a password and rotate immediately if exposed. Rate limiting
          is planned before the public Zapier app listing goes live.
        </li>
      </UL>
    </>
  )
}

function Operational() {
  return (
    <>
      <H2 id="operational">Operational notes</H2>

      <H3>Multi-instance safety</H3>
      <P>
        The in-process booking lock serializes same-booking events within one
        Node process so a parallel <Inline>new</Inline> +{" "}
        <Inline>updated</Inline> can&apos;t double-insert. The current
        single-container Railway deploy is safe. If horizontal scaling is
        enabled, the lock needs to move to Redis or a Postgres advisory lock.
      </P>

      <H3>Historical imports</H3>
      <P>
        The Zapier integration is for live sync only. For a one-shot load of
        historical bookings, use the BK CSV importer (SF Settings &rarr;
        Imports &rarr; BK). Both paths write the same{" "}
        <Inline>contact_info.external_id</Inline>, so bookings touched by either
        path share identity and won&apos;t duplicate.
      </P>

      <H3>Independent of LeadBridge</H3>
      <P>
        BK &rarr; SF is a separate integration from LeadBridge &rarr; BK (which
        is also a Zapier integration). The two run side-by-side without
        interference. SF&apos;s jobs table carries{" "}
        <Inline>contact_info.source = &quot;bookingkoala&quot;</Inline> for BK-sourced
        jobs and <Inline>lb_external_request_id</Inline> when the customer
        originally came in through LB.
      </P>

      <H3>Retiring the integration</H3>
      <ol className="list-decimal pl-6 my-3 space-y-1.5 text-[14.5px] text-slate-700 leading-7">
        <li><Inline>DELETE /api/integrations/bookingkoala/api-key</Inline> (revokes the key).</li>
        <li>Delete <Inline>bookingkoala-zapier.js</Inline> and its one-line mount in <Inline>server.js</Inline>.</li>
        <li>Optional: run <Inline>084_bookingkoala_zapier_down.sql</Inline> to drop the table &amp; column.</li>
      </ol>
      <P>Already-synced jobs remain in SF unchanged.</P>
    </>
  )
}

// ─ Page ────────────────────────────────────────────────────────────

export default function DocsZapierPage() {
  const [search, setSearch] = useState("")
  const [activeId, setActiveId] = useState("overview")

  // Scroll-spy: observe each section and highlight the one in view.
  // Root is the main content column (its own scrollable region) so this
  // works with the fixed-viewport layout where the window itself doesn't
  // scroll.
  useEffect(() => {
    const root = document.getElementById("docs-main")
    const ids = FLAT_SECTIONS.map((s) => s.id)
    const io = new IntersectionObserver(
      (entries) => {
        const visible = entries.filter((e) => e.isIntersecting)
        if (visible.length > 0) {
          visible.sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top)
          setActiveId(visible[0].target.id)
        }
      },
      { root: root || null, rootMargin: "0px 0px -70% 0px", threshold: 0.01 },
    )
    for (const id of ids) {
      const el = document.getElementById(id)
      if (el) io.observe(el)
    }
    return () => io.disconnect()
  }, [])

  // Give the page its own light theme regardless of what the app sets.
  useEffect(() => {
    const prev = document.body.style.background
    document.body.style.background = "#ffffff"
    return () => { document.body.style.background = prev }
  }, [])

  // Make in-page #anchor navigation scroll the main content column
  // instead of the window — the window doesn't scroll under the
  // fixed-viewport layout.
  useEffect(() => {
    const root = document.getElementById("docs-main")
    if (!root) return
    const onClick = (e) => {
      const a = e.target.closest && e.target.closest('a[href^="#"]')
      if (!a) return
      const id = a.getAttribute("href").slice(1)
      if (!id) return
      const target = document.getElementById(id)
      if (!target) return
      e.preventDefault()
      target.scrollIntoView({ behavior: "smooth", block: "start" })
      // Keep the URL hash in sync so refreshes land on the same section.
      if (window.history.replaceState) {
        window.history.replaceState(null, "", `#${id}`)
      }
    }
    document.addEventListener("click", onClick)
    // Deep link: scroll on first paint if URL already has a hash.
    const initialHash = window.location.hash.slice(1)
    if (initialHash) {
      const el = document.getElementById(initialHash)
      if (el) el.scrollIntoView({ block: "start" })
    }
    return () => document.removeEventListener("click", onClick)
  }, [])

  return (
    <div
      className="h-screen flex flex-col overflow-hidden bg-white text-slate-900"
      style={{ fontFamily: "ui-sans-serif, system-ui, -apple-system, Segoe UI, Roboto, sans-serif" }}
    >
      <Header />
      <div className="flex-1 flex items-stretch max-w-[1440px] mx-auto w-full overflow-hidden">
        <Sidebar activeId={activeId} search={search} onSearch={setSearch} />
        <main
          id="docs-main"
          className="flex-1 min-w-0 h-full overflow-y-auto px-6 lg:px-10 py-10"
        >
          <div className="max-w-[760px]">
            <Overview />
            <QuickStart />
            <BaseUrls />
            <Authentication />
            <ZapierSetup />
            <EventTypes />
            <PayloadFields />
            <Endpoints />
            <ResponseSchema />
            <ErrorCodes />
            <IdentityResolution />
            <StatusMapping />
            <TestingCurl />
            <Troubleshooting />
            <Security />
            <Operational />

            <div className="mt-16 pt-8 border-t border-slate-200 text-[12.5px] text-slate-500">
              <p>
                Something unclear or missing? Open an issue in{" "}
                <a
                  href="https://github.com/Geos-LLC/service-flow-backend"
                  className="text-sky-600 hover:underline"
                  target="_blank"
                  rel="noreferrer"
                >
                  Geos-LLC/service-flow-backend
                </a>
                .
              </p>
            </div>
          </div>
        </main>
        <RightToc activeId={activeId} />
      </div>
    </div>
  )
}
